import { useCallback, useEffect } from 'react';

import { useSnackbar } from 'notistack';

import { PromoRole } from '~/services/PromoService';

import ApiError from '~/types/ApiError';

import { useGetUserPromoRoles } from './apis/promo/useGetUserPromoRoles';

type UseUserPromoRolesProps = {
  userId: string;
  enabled?: boolean;
};

type UseUserPromoRolesResult = {
  isLoading: boolean;
  hasLoaded: boolean;
  roles: Array<PromoRole>;
  error: Error | null;
  refetch: () => Promise<void>;
  hasRole: (roleName: string) => boolean;
};

const EMPTY_ROLES: Array<PromoRole> = [];

export function useUserPromoRoles({ userId, enabled = true }: UseUserPromoRolesProps): UseUserPromoRolesResult {
  const { enqueueSnackbar } = useSnackbar();
  const query = useGetUserPromoRoles(userId, enabled);
  const roles = query.data ?? EMPTY_ROLES;
  const error = query.error;

  useEffect(() => {
    if (!error) {
      return;
    }

    const isAuthError = error instanceof ApiError && (error.status === 401 || error.status === 403);
    if (!isAuthError) {
      enqueueSnackbar(`Error fetching user promo roles: ${error.message}`, { variant: 'error' });
    }
  }, [enqueueSnackbar, error]);

  const fetchUserRoles = useCallback(async () => {
    if (enabled && userId) {
      await query.refetch();
    }
  }, [enabled, userId, query.refetch]);

  const hasRole = useCallback(
    (roleName: string): boolean => {
      return roles.some((role) => role.roleName === roleName && role.isActive);
    },
    [roles],
  );

  return {
    isLoading: enabled && Boolean(userId) && query.isPending,
    hasLoaded: enabled && Boolean(userId) && query.isSuccess,
    roles,
    error,
    refetch: fetchUserRoles,
    hasRole,
  };
}
